import { atom } from "../app-jotai";

// App-level authentication state. `/api/me` is the client's bootstrap: it returns
// the signed-in user + their workspaces, or 401 for an anonymous visitor (prod
// with REQUIRE_AUTH). In local dev the server's bypass returns a synthetic
// "Local Dev" user (dev@localhost) — real for API purposes, but not a real Google
// login, so `isRealUser` treats it as not-signed-in for account UI display.

export interface Workspace {
  id: number;
  muid: string;
  name: string;
  slug?: string;
  role: string;
  historyMode: string;
}

export interface Me {
  user: {
    id: number;
    name: string | null;
    email: string | null;
    picture?: string | null;
  } | null;
  activeWorkspaceId: number | null;
  workspaces: Workspace[];
}

// null = anonymous (or not yet loaded — pair with meLoadedAtom to distinguish).
export const meAtom = atom<Me | null>(null);
export const meLoadedAtom = atom<boolean>(false);

/** Fetch `/api/me`; 401 (anonymous) resolves to null rather than throwing. */
export const fetchMe = async (): Promise<Me | null> => {
  try {
    const res = await fetch("/api/me");
    if (res.status === 401) {
      return null;
    }
    if (!res.ok) {
      return null;
    }
    return (await res.json()) as Me;
  } catch {
    return null;
  }
};

/** A genuine Google account, as opposed to anonymous or the local-dev bypass. */
export const isRealUser = (me: Me | null): boolean =>
  !!me?.user && me.user.email !== "dev@localhost";
